import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, styled, Typography } from '@ori-ui/mui';
import type { GlobalSharedMetadataValidationStringValidationDto } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { ChangeEventHandler, FC, FocusEventHandler, ReactElement } from 'react';
import { Controller } from 'react-hook-form';

import type { ILabelMapper } from '../../../Localization';
import { Localization } from '../../../Localization';
import { useFormError } from '../../../contexts';
import { getErrorLocalizationByCode, renderValidationMessage } from '../../../utils';

interface ISelectOption {
  label: ReactElement | string;
  value: number | string;
}

interface ISelectFormFieldByConfigProps {
  labelMapper?: ILabelMapper;
  options?: ISelectOption[];
  name: string;
  label?: ReactElement | string;
  tooltipText?: ReactElement | string;
  title?: ReactElement | string;
  placeholder?: ReactElement | string;
  isEditable?: boolean;
  isAvailable?: boolean;
  displayOrder?: number;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  availableOptions?: string[];
  validation?: GlobalSharedMetadataValidationStringValidationDto;
  isAddressMatchRequired?: boolean;
  initialValue?: number | string;
}

const SelectWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(0),
}));

export const SelectFormFieldByConfig: FC<ISelectFormFieldByConfigProps> = (props) => {
  const {
    name,
    label,
    title,
    isEditable,
    isAvailable,
    displayOrder,
    options,
    validation,
    initialValue,
    isAddressMatchRequired,
    placeholder,
    labelMapper,
    ...selectField
  } = props;

  const { getTestIdProps, getTestId } = useTestId();

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const submitError = customErrors?.find((error) => error.fieldName?.toLowerCase() === name.toLowerCase());

  const errorMessage = getErrorLocalizationByCode(submitError?.errorCode);

  const regexPattern = validation?.regularExpression ? new RegExp(validation.regularExpression) : undefined;

  const renderSelectOption = (item: ISelectOption, index: number): ReactElement => (
    <MenuItem
      key={index}
      value={item.value}
      {...getTestIdProps('select-field-option')}
    >
      {item.label}
    </MenuItem>
  );

  const availableOptions = selectField.availableOptions?.map((opt) => ({
    label: labelMapper ? labelMapper.getLocalizationKey(opt) : opt,
    value: opt,
  }));
  const currentOptions = availableOptions?.length ? availableOptions : options;

  // we need start tabindex from 1 but displayOrder starts from 0
  const correctTabIndex = typeof displayOrder === 'number' ? displayOrder + 1 : displayOrder;

  const renderField = () => (
    <Grid
      item
      xs={12}
      sm={12}
      style={{ order: displayOrder, textAlign: 'left' }}
    >
      <Box
        margin="0 auto 25px"
        maxWidth={500}
      >
        {title ? (
          <Typography
            variant="body1"
            color="textPrimary"
            sx={{ fontWeight: 'bold', marginBottom: '10px', textAlign: 'left' }}
            {...getTestIdProps(`${name}-select-field-title`)}
          >
            {title}
          </Typography>
        ) : null}
        <Controller
          name={name}
          rules={{
            required: validation?.isMandatory,
            maxLength: validation?.maxLength,
            minLength: validation?.minLength,
            pattern: regexPattern,
          }}
          defaultValue={initialValue}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              error={!!fieldState.error}
              required={validation?.isMandatory}
            >
              <InputLabel id={`${name}-label`}>{label}</InputLabel>
              <SelectWrapper>
                <Select
                  {...field}
                  fullWidth
                  variant="outlined"
                  label={label}
                  labelId={`${name}-label`}
                  error={!!fieldState.error}
                  required={validation?.isMandatory}
                  disabled={!isEditable}
                  inputProps={{
                    tabIndex: correctTabIndex,
                    readOnly: isAddressMatchRequired ?? false,
                    'data-testid': getTestId(`${name}-select-field`),
                  }}
                >
                  <MenuItem
                    disabled
                    value={undefined}
                  >
                    <FormHelperText>{placeholder}</FormHelperText>
                  </MenuItem>
                  {currentOptions?.map(renderSelectOption)}
                </Select>
              </SelectWrapper>
              {fieldState.error ? (
                <FormHelperText error>{renderValidationMessage(fieldState.error)}</FormHelperText>
              ) : null}
              {submitError ? (
                <FormHelperText error>
                  <Localization {...errorMessage} />
                </FormHelperText>
              ) : null}
            </FormControl>
          )}
        />
      </Box>
    </Grid>
  );

  const fieldComponent = isAvailable && renderField();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need fragment
    <>{fieldComponent}</>
  );
};
